// DailyWeb.js
import React, { useState, useCallback, useRef, useEffect } from "react";
import ForceGraph3D from "react-force-graph-3d";
import socket from "../../socket";
import { motion } from "framer-motion";
import classes from "./DailyWeb.module.css";
import { useLocation } from "react-router-dom";
import { CSS2DRenderer, CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import * as d3 from "d3-force";   // ✅ import d3-force so we can configure forces
import DailyHUD from "./DailyHUD/DailyHUD";
import { Info } from "lucide-react";

const getRandomPastelColor = () => {
  const hue = Math.floor(Math.random() * 360); // random hue 0–359
  const saturation = 70 + Math.random() * 20;  // 70–90%
  const lightness = 70 + Math.random() * 15;   // 70–85%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};


const DailyWeb = ({ user }) => {
  const [graphData, setGraphData] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [answer, setAnswer] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const fgRef = useRef();
  const location = useLocation();
  const [lockedNodeId, setLockedNodeId] = useState(null);
  const [showHud, setShowHud] = useState(true);
  // ---- socket init
  useEffect(() => {
    if (location.pathname !== "/dailyweb") return;

    const handleInit = (data, ackFn) => {
      setGraphData(data);
      if (typeof ackFn === "function") ackFn("ok");
    };

    socket.on("init", handleInit);
    socket.emit("request-init", (res) => {
      console.log("Init requested:", res);
    });

    return () => {
      socket.off("init", handleInit);
    };
  }, [location]);

  useEffect(() => {
  const handleBeforeUnload = () => {
    if (lockedNodeId) {
      socket.emit("unlock-node", lockedNodeId);
    }
  };

  window.addEventListener("beforeunload", handleBeforeUnload);

  return () => {
    // also handle React unmount
    if (lockedNodeId) {
      socket.emit("unlock-node", lockedNodeId);
    }
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
}, [lockedNodeId]);

  // ---- graph forces: spread out + minimal movement
  useEffect(() => {
    if (fgRef.current) {
      // define charge and link forces if not already
      fgRef.current.d3Force("charge", d3.forceManyBody().strength(-300)); // repulsion
      fgRef.current.d3Force("link", d3.forceLink().distance(200));        // link length
      fgRef.current.d3Force("center", d3.forceCenter(0, 0, 0));           // keep centered
    }
  }, [graphData]);

  // ---- socket updates
  useEffect(() => {
    const handleUpdate = (data) => {
      setGraphData((prev) => {
        if (!prev) return data;

        // preserve positions between updates
        const mergedNodes = data.nodes.map((newNode) => {
  const oldNode = prev.nodes.find((n) => n.id === newNode.id);
  if (oldNode) return { ...newNode, x: oldNode.x, y: oldNode.y, z: oldNode.z };

  // if it's a brand new node, try to place near one of its neighbors
  const neighborLink = data.links.find(l => l.source === newNode.id || l.target === newNode.id);
  if (neighborLink) {
    const neighborId = neighborLink.source === newNode.id ? neighborLink.target : neighborLink.source;
    const neighbor = prev.nodes.find(n => n.id === neighborId);
    if (neighbor) {
      return {
        ...newNode,
        color: getRandomPastelColor(),
        x: neighbor.x + (Math.random() - 0.5) * 50,
        y: neighbor.y + (Math.random() - 0.5) * 50,
        z: neighbor.z + (Math.random() - 0.5) * 50,
      };
    }
  }

  // fallback: random scatter so it’s not at 0,0,0
  return {
    ...newNode,
    x: (Math.random() - 0.5) * 500,
    y: (Math.random() - 0.5) * 500,
    z: (Math.random() - 0.5) * 500,
  };
});

        return { nodes: mergedNodes, links: data.links };
      });
    };

    socket.on("update", handleUpdate);
    return () => {
      socket.off("update", handleUpdate);
    };
  }, []);

  // ---- WASD to pan (no THREE.js math) ----
useEffect(() => {
  const fg = fgRef.current;
  if (!fg) return;

  const moveSpeed = 80;

  const handleKeyDown = (e) => {
    const cam = fg.cameraPosition();   // { x, y, z }
    let { x, y, z } = cam;
    switch (e.key.toLowerCase()) {
      case "w":
        y += moveSpeed;  // pan up
        break;
      case "s":
        y -= moveSpeed;  // pan down
        break;
      case "a":
        x -= moveSpeed;  // pan left
        break;
      case "d":
        x += moveSpeed;  // pan right
        break;
      default:
        return;
    }
    // instantly move the camera
    let z_5 = z - 500;
    fg.cameraPosition({ x, y, z }, { x, y, z_5 }, 1000);
  };

  window.addEventListener("keydown", handleKeyDown);
  return () => window.removeEventListener("keydown", handleKeyDown);
}, []);


  // ---- node click
  const handleNodeClick = useCallback((node) => {
  if (!fgRef.current) return;

  // Unlock if switching nodes
  if (lockedNodeId && lockedNodeId !== node.id) {
    socket.emit("unlock-node", lockedNodeId, (res) => {
      if (res.success) {
        console.log("Previous node unlocked");
        setLockedNodeId(null);
      }
    });
  }

  // If already locked by me, just zoom in
  if (lockedNodeId === node.id) {
    fgRef.current.cameraPosition(
      { x: node.x, y: node.y },
      { x: node.x, y: node.y },
      2000
    );
    return;
  }

  // Lock only unanswered nodes
  if (!node.answer) {
    socket.emit("lock-node", { nodeId: node.id, username: user?.username }, (res) => {
      if (res.success) {
        console.log("Node locked successfully");
        setLockedNodeId(node.id);
        setSelectedNode(node);
        setAnswer(node.answer || "");
        fgRef.current.cameraPosition(
          { x: node.x, y: node.y, z: node.z + 300 },
          { x: node.x, y: node.y, z: node.z },
          2000
        );
      } else {
        alert(`Could not lock node: ${res.reason}`);
      }
    });
  } else {
    // Just zoom into answered nodes
    setSelectedNode(node);
    fgRef.current.cameraPosition(
      { x: node.x, y: node.y, z: node.z + 300 },
      { x: node.x, y: node.y, z: node.z },
      2000
    );
  }
}, [lockedNodeId]);


  const handleSubmitAnswer = async () => {
    if (!answer.trim() || !selectedNode) return;
    setIsLoading(true);
    socket.emit("answer-node", { 
    nodeId: selectedNode.id, 
    answer, 
    username: user?.username 
    }, (res) => {
      // Callback runs when server acknowledges
      if (res.success) {
        setSelectedNode(null);
        setAnswer("");
        setIsLoading(false);  // ✅ stop spinner only after response
      } else {
        alert("Error submitting answer: " + res.reason);
      }
    });
  };

  // ---- build HTML node card
  const nodeThreeObject = useCallback(
  (node) => {
    const div = document.createElement("div");
    div.className = classes.nodeCard;
    div.style.minWidth = "140px";
    div.style.maxWidth = "240px";
    div.style.padding = "8px 12px";
    div.style.border = "2px solid " + (node.locked ? "red" : "#ccc");
    div.style.borderRadius = "12px";
    div.style.background = node.answer ? "#e3f2fd" : "white";
    div.style.boxShadow = node.locked
      ? "0 0 12px rgba(255,0,0,0.6)"
      : "0 2px 6px rgba(0,0,0,0.15)";
    div.style.fontSize = "13px";
    div.style.lineHeight = "1.4";
    div.style.pointerEvents = "none";

    const q = document.createElement("div");
    q.innerText = node.question;
    q.style.fontWeight = "600";
    q.style.marginBottom = "6px";
    div.appendChild(q);

    if (node.answer) {
      // ✅ Answered: just show the answer, no lock label
      const a = document.createElement("div");
      a.innerText = `[${node.answeredBy || "Anon"}]: ${node.answer}`;
      a.style.marginTop = "4px";
      a.style.color = "#333";
      div.appendChild(a);
    } else {
      // ✅ Not answered yet
      if (node.locked) {
        const lockLabel = document.createElement("div");
        if (node.lockedBy === user?.username  || node.lockedBySocket === socket.id) {
          lockLabel.innerText = "Answering…";
          lockLabel.style.color = "green";
        }  else {
          lockLabel.innerText = `🔒 In use by ${node.lockedBy}`;
          lockLabel.style.color = "red";
        }
        div.appendChild(lockLabel);
      } else {
        const btn = document.createElement("button");
        btn.innerText = "Answer";
        btn.onclick = () => handleNodeClick(node);
        btn.style.pointerEvents = "auto";
        btn.style.marginBottom = "6px";
        btn.style.alignSelf = "flex-end";
        div.style.display = "flex";
        div.style.flexDirection = "column";
        div.appendChild(btn);
      }
    }

    return new CSS2DObject(div);
  },
  [lockedNodeId]
);


  if (!graphData) return (<div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: "1rem",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            background: `
              radial-gradient(circle at 30% 30%, 
                rgba(255,255,255,0.9), 
                rgba(0,112,243,0.8) 40%, 
                rgba(0,0,0,0.25) 100%)`,
          }}
        />
        <p style={{ fontSize: "1rem", color: "#555", fontWeight: 500 }}>
          ... Loading ...
        </p>
      </div>);

  return (
    <div className={classes.ideaMapContainer}>
      <ForceGraph3D
        ref={fgRef}
        graphData={graphData}
        nodeThreeObject={nodeThreeObject}
        linkWidth={1}
        linkColor={() => "#999"}
        backgroundColor="#f8f9fa"
        onNodeClick={handleNodeClick}
        nodeRelSize={6}
        rendererConfig={{ antialias: true }}
        extraRenderers={[new CSS2DRenderer()]} // ✅ required for HTML nodes
        cooldownTicks={50}  // ✅ stops simulation after 50 iterations (minimal movement)
      />

        {showHud && <DailyHUD fgRef={fgRef} onClose={() => setShowHud(false)} />}
      {!showHud && (
        <motion.div
          className={classes.reopenHud}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          onClick={() => setShowHud(true)}
        >
              <Info size={18} style={{ marginTop: "6px" }} />
        </motion.div>
      )}


      {selectedNode && (
        <div
          className={`${classes.replyBar} ${
            selectedNode ? classes.show : classes.hide
          }`}
        >
          <motion.div
            className={classes.replyInner}
            animate={{ width: isLoading ? "44px" : "100%" }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            style={{
              overflow: "hidden",
              padding: isLoading ? "0.25rem" : "0.75rem",
              borderRadius: isLoading ? "0px" : "15px",
              border: isLoading ? "none" : "1px solid #ccc",
              background: isLoading
                ? "linear-gradient(to right, #e0e0e0, #ffffff)"
                : "white",
            }}
          >
            {!isLoading ? (
              <>
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder={`Answer: ${selectedNode.question}`}
                  className={classes.replyInput}
                  rows={2}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (answer.trim() && !isLoading) {
                        handleSubmitAnswer();
                      }
                    }
                  }}
                />
                <button
                  className={classes.sendButton}
                  onClick={handleSubmitAnswer}
                  disabled={!answer.trim() || isLoading}
                >
                  ➤
                </button>
              </>
            ) : (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: `
                    radial-gradient(circle at 30% 30%, 
                      rgba(255,255,255,0.9), 
                      rgba(0,112,243,0.8) 40%, 
                      rgba(0,0,0,0.25) 100%)`,
                }}
              />
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default DailyWeb;
